import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Eye, Download, Trash2, ExternalLink, Copy, Share2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function MyPacks() {
  const nav = useNavigate();
  const [packs, setPacks] = useState(null);

  const load = async () => {
    const { data } = await api.get("/shared-packs/mine/list");
    setPacks(data.packs);
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (slug, title) => {
    if (!confirm(`Unpublish "${title}"? The public link will stop working immediately.`)) return;
    try {
      await api.delete(`/shared-packs/${slug}`);
      toast.success("Pack unpublished");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't unpublish");
    }
  };

  const copyLink = (slug) => {
    const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const url = `${backend.replace(/\/+$/, "")}/api/p/${slug}`;
    navigator.clipboard?.writeText(url);
    toast.success("Link copied");
  };

  const totalViews = (packs || []).reduce((a, p) => a + (p.views || 0), 0);
  const totalImports = (packs || []).reduce((a, p) => a + (p.imports || 0), 0);

  return (
    <div className="max-w-[1180px] mx-auto px-5 md:px-8 py-10 md:py-14">
      <button
        onClick={() => nav(-1)}
        className="text-[13px] font-medium text-[#B0AEBC] hover:text-white flex items-center gap-1 mb-8"
        data-testid="my-packs-back"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-start justify-between flex-wrap gap-4 mb-8"
      >
        <div>
          <div className="eyebrow">My published packs</div>
          <h1 className="display-2 mt-2">Shared <span className="grad-text">studies</span></h1>
          <p className="text-[15px] text-[#B0AEBC] mt-3 max-w-lg">
            Anyone with the link can view and import your packs. They're snapshots — editing a study doesn't change a published pack.
          </p>
        </div>
        {packs && packs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-4 surface px-5 py-3.5"
            style={{ boxShadow: "0 0 24px -8px rgba(124,92,255,0.45)" }}
            data-testid="packs-totals"
          >
            <div className="text-center">
              <div className="text-[22px] font-bold tabular-nums text-white">{totalViews}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#8E8B99] mt-0.5">Views</div>
            </div>
            <div className="w-px h-9 bg-white/[0.08]" />
            <div className="text-center">
              <div className="text-[22px] font-bold tabular-nums text-white">{totalImports}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#8E8B99] mt-0.5">Imports</div>
            </div>
          </motion.div>
        )}
      </motion.header>

      {!packs && (
        <div className="grid sm:grid-cols-2 gap-3">
          {[0,1,2].map((i) => <div key={i} className="h-32 shimmer rounded-2xl" />)}
        </div>
      )}

      {packs && packs.length === 0 && (
        <div className="surface p-10 text-center" data-testid="empty-packs">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#7C5CFF]/25 to-[#5AC8FA]/15 border border-white/10 flex items-center justify-center mx-auto mb-4" style={{ boxShadow: "0 0 24px -8px rgba(124,92,255,0.55)" }}>
            <Share2 size={20} className="text-[#B8A4FF]" />
          </div>
          <h3 className="text-[17px] font-semibold text-white">No packs published yet</h3>
          <p className="text-[13.5px] text-[#B0AEBC] mt-2 max-w-sm mx-auto">
            Open any study and tap the share icon to publish a snapshot. Anyone can view it; logged-in users can import it.
          </p>
          <Link to="/app/studies">
            <button className="btn btn-primary mt-5 px-5 py-2.5 text-[13.5px]">
              <Sparkles size={13} /> Pick a study to share
            </button>
          </Link>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <AnimatePresence>
          {(packs || []).map((p, i) => (
            <motion.div
              key={p.slug}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, height: 0 }}
              transition={{ delay: i * 0.04 }}
              whileHover={{ y: -3 }}
              className="surface p-5 relative overflow-hidden"
              data-testid={`my-pack-${p.slug}`}
            >
              <div
                className="absolute -top-12 -right-12 w-32 h-32 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(124,92,255,0.30), transparent 65%)", filter: "blur(20px)" }}
              />
              <div className="relative">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/packs/${p.slug}`} target="_blank" className="block min-w-0 flex-1">
                    <h3 className="text-[15.5px] font-semibold text-white hover:text-[#B8A4FF] transition-colors truncate">{p.title}</h3>
                    <div className="text-[11.5px] text-[#8E8B99] mt-1 font-mono tracking-wider">/packs/{p.slug}</div>
                  </Link>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => copyLink(p.slug)}
                      title="Copy link"
                      className="w-7 h-7 rounded-lg hover:bg-white/10 flex items-center justify-center text-[#B0AEBC] hover:text-white"
                      data-testid={`copy-${p.slug}`}
                    >
                      <Copy size={12} />
                    </button>
                    <Link
                      to={`/packs/${p.slug}`}
                      target="_blank"
                      title="Open"
                      className="w-7 h-7 rounded-lg hover:bg-white/10 flex items-center justify-center text-[#B0AEBC] hover:text-white"
                      data-testid={`open-${p.slug}`}
                    >
                      <ExternalLink size={12} />
                    </Link>
                    <button
                      onClick={() => onDelete(p.slug, p.title)}
                      title="Unpublish"
                      className="w-7 h-7 rounded-lg hover:bg-white/10 flex items-center justify-center text-[#B0AEBC] hover:text-[#B8A4FF]"
                      data-testid={`delete-${p.slug}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
                {p.description && (
                  <p className="text-[12.5px] text-[#B0AEBC] mt-3 line-clamp-2">{p.description}</p>
                )}
                <div className="flex items-center gap-4 mt-4 pt-3 border-t border-white/[0.06]">
                  <div className="flex items-center gap-1.5 text-[12.5px] text-[#B0AEBC]">
                    <Eye size={12} />
                    <span className="tabular-nums font-semibold text-white">{p.views || 0}</span>
                    <span className="text-[10.5px] uppercase tracking-wider text-[#8E8B99]">views</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[12.5px] text-[#B0AEBC]">
                    <Download size={12} className="text-[#5AC8FA]" />
                    <span className="tabular-nums font-semibold text-white">{p.imports || 0}</span>
                    <span className="text-[10.5px] uppercase tracking-wider text-[#8E8B99]">imports</span>
                  </div>
                  <div className="text-[11px] text-[#8E8B99] ml-auto">
                    {p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
